import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Outlet, useLocation, useParams } from 'react-router-dom'

import { useAuth } from '@/middlewares/auth/auth-context'
import LoadingScreen from '@/components/common/LoadingScreen'
import { getEventMenuItems, getVisibleEventMenu } from '@/components/EventHub/settings/eventMenu.items'
import { EVENT_HUB_TABS } from '@/components/EventHub/tabs.config'
import useScreenTop from '@/hooks/useScreenTop'
import { OrganizerTour } from '@/components/Organizer/Tours'
import { OrganizerShellProvider } from './OrganizerShellContext'
import OrgSideMenu from './OrgSideMenu'
import useOrgEvent from './useOrgEvent'
import useOrgSideState from './useOrgSideState'
import useOrgStatusToggles from './useOrgStatusToggles'
import { buildOrgMenu } from './orgMenu.model'
import { getActiveMenuEntry, relativeEventPath } from './orgMenu.active'

import './index.css'

/** Id of the slot the aside is portaled into: first child of `.app-shell` (App.js). */
export const ORG_SIDE_SLOT_ID = 'orgSideSlot'

const HUB_TAB_PATHS = EVENT_HUB_TABS.map((tab) => tab.path).filter(Boolean)

/**
 * Layout route of every organizer page (mock `_kit/orgside.*`, 6 Oct 2026): the event hub in
 * organizer mode, Live registrations and the legacy organizer pages. Fetches the event summary
 * (/event/menu), decides who gets the menu — the owner / an admin, else a judge, a check-in
 * attendant or a partner with their reduced lists — and renders the side menu through a portal
 * into `#orgSideSlot`, the first grid column of `.app-shell`. Public visitors and athletes get a
 * plain <Outlet/>: nothing applies without `body.osd-on`, the public hub stays pixel-identical.
 */
export default function OrganizerShell() {
    const { eventIdentifier } = useParams()
    const { pathname } = useLocation()
    const authContext = useAuth()

    const base = pathname.startsWith('/sporthub/') ? `/sporthub/${eventIdentifier}` : `/${eventIdentifier}`
    const rel = relativeEventPath(pathname, base)
    const isHub = rel === '' || HUB_TAB_PATHS.includes(rel)

    const { event, loading, patch } = useOrgEvent(eventIdentifier, { enabled: !!authContext.isLoggedIn })

    const userId = authContext.user?.id
    const viewer = useMemo(() => {
        if (!event) return null
        const isOrganizer = !!authContext.isAdmin || (!!userId && userId === event.orgaId)
        const isJudgeOnly = !isOrganizer && !!authContext.isJudge?.(event.judge_account)
        const isCheckInAttendantOnly = !isOrganizer && !!authContext.isCheckInAttendant?.(event.checkInAttendants)
        const isPartnerForEvent =
            !isOrganizer &&
            !!authContext.isPartner &&
            !!authContext.partnerEvents?.some((partnerEvent) => String(partnerEvent._id) === String(event._id))
        return { isOrganizer, isJudgeOnly, isCheckInAttendantOnly, isPartnerForEvent }
    }, [event, userId, authContext.isAdmin, authContext.isPartner, authContext.partnerEvents])

    const groups = useMemo(() => {
        if (!event || !viewer) return []
        const hasRole =
            viewer.isOrganizer || viewer.isJudgeOnly || viewer.isCheckInAttendantOnly || viewer.isPartnerForEvent
        if (!hasRole) return []
        const items = getEventMenuItems(viewer)
        return buildOrgMenu(
            getVisibleEventMenu(items, event, { isAdmin: !!authContext.isAdmin, isAdminOrOrga: viewer.isOrganizer }),
        )
    }, [event, viewer, authContext.isAdmin])

    const enabled = groups.length > 0
    const side = useOrgSideState({ enabled })

    const onStatusChange = useCallback(
        (key, on) => patch((previous) => ({ ...previous, status: { ...(previous.status || {}), [key]: on } })),
        [patch],
    )
    const toggles = useOrgStatusToggles(event, onStatusChange)
    const setStatus = useCallback((key, on) => toggles[key]?.(on), [toggles])
    const setIdentity = useCallback((identity) => patch((previous) => ({ ...previous, ...identity })), [patch])

    const active = useMemo(() => getActiveMenuEntry(rel, groups, HUB_TAB_PATHS), [rel, groups])

    // Organizer pages without a banner go back to the top on every screen change (mock screenTop);
    // the hub owns its own call (anchored on its tab bar).
    useScreenTop(isHub ? null : pathname)

    const [slot, setSlot] = useState(null)
    useEffect(() => {
        setSlot(document.getElementById(ORG_SIDE_SLOT_ID))
    }, [])

    const contextValue = useMemo(
        () => ({
            eventIdentifier,
            base,
            rel,
            event,
            status: event?.status || null,
            setStatus,
            // mirror a flag the page wrote itself (volunteering follows its dates since 14 Oct 2026)
            patchStatus: onStatusChange,
            setIdentity,
            viewer,
            menu: groups,
            enabled,
            isHub,
        }),
        [eventIdentifier, base, rel, event, setStatus, onStatusChange, setIdentity, viewer, groups, enabled, isHub],
    )

    // one short wait on the first organizer page of a session (cached afterwards): no layout jump
    // from the public layout to the grid once the summary lands
    if (loading) return <LoadingScreen />

    return (
        <OrganizerShellProvider value={contextValue}>
            {enabled &&
                slot &&
                createPortal(
                    <OrgSideMenu
                        side={side}
                        groups={groups}
                        base={base}
                        active={active}
                        event={event}
                        setCompetition={patch}
                        tour={!!viewer?.isOrganizer}
                    />,
                    slot,
                )}
            {enabled && <OrganizerTour />}
            <Outlet />
        </OrganizerShellProvider>
    )
}
